import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import { validators, sanitizers } from "../utils/validation";
import { loginLimiter, formatRetryTime } from "../utils/rateLimit";

// =============================================================================
// LOGIN PAGE
// =============================================================================
// PURPOSE: Email/password authentication via Supabase Auth.
//
// REQUIREMENTS:
// - Email and password input with validation
// - Session persistence (Supabase handles localStorage)
// - Retry on cold start (Supabase free tier wakes from sleep)
// - Rate limiting (5 attempts per 15 min)
// - Redirect to dashboard on success
//
// KEY DECISIONS:
// - 45s watchdog timer prevents infinite loading (2 retries @ 30s max)
// - AuthContext handles profile fetch and sets isAuthenticated
// =============================================================================

const LoginPage: React.FC = () => {
  const { signIn, isAuthenticated, authError } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Where a logged-out click was headed (set by App's admin redirect). Only
  // in-app /admin paths are honoured, never an outside URL.
  const location = useLocation();
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const from = typeof fromState === "string" && fromState.startsWith("/admin/") ? fromState : null;

  // If already authenticated, redirect immediately
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from ?? "/admin/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Derive display error and loading state from auth context
  const displayError = authError || error;
  const isLoading = loading && !authError;

  // Safety Watchdog: If loading takes > 45s, force stop it.
  // The AuthContext profile fetch can take up to 30s per attempt + 1 retry.
  useEffect(() => {
    let watchdog: ReturnType<typeof setTimeout>;
    if (loading) {
      watchdog = setTimeout(() => {
        if (loading) {
          setLoading(false);
          setError(
            "Login timed out. Please try again — if this keeps happening, Supabase may be waking up from sleep (free tier)."
          );
        }
      }, 45000);
    }
    return () => clearTimeout(watchdog);
  }, [loading]);

  const handleSubmit = async (
    e?: React.FormEvent,
    overrideEmail?: string,
    overridePass?: string
  ) => {
    if (e) e.preventDefault();
    setError("");

    // Check rate limit BEFORE setting loading
    const rateLimit = loginLimiter();
    if (!rateLimit.allowed) {
      const retryTime = formatRetryTime(rateLimit.retryAfter ?? 0);
      setError(`Too many login attempts. Please try again in ${retryTime}.`);
      return;
    }

    setLoading(true);

    // Sanitize and validate inputs
    const targetEmail = sanitizers.sanitizeString(overrideEmail || email).toLowerCase();
    const targetPass = overridePass || password;

    if (!targetEmail || !targetPass) {
      setError("Please enter both email and password.");
      setLoading(false);
      return;
    }

    // Validate email format
    if (!validators.email(targetEmail)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      const { error } = await signIn(targetEmail, targetPass);
      if (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof error === "object" && error !== null && "message" in error
              ? String((error as { message: unknown }).message)
              : "Failed to sign in";
        setError(errorMessage);
        setLoading(false);
      }
      // signIn succeeded — Supabase session is active.
      // AuthContext's onAuthStateChange listener will call fetchUserProfile,
      // which sets `isAuthenticated` and triggers the redirect above.
      // Keep loading=true so the button shows "Signing in..." until redirect.
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown";
      setError("An unexpected error occurred: " + errorMessage);
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to={from ?? "/admin/dashboard"} replace />;
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm animate-rise">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-blue-600/20">
            <span className="text-2xl font-extrabold text-white tracking-tight">A</span>
          </div>
          <h1 className="heading-display text-3xl text-glass mb-1">Altara</h1>
          <p className="text-sm text-glass-muted">Dynamis Revenue Operations</p>
        </div>

        {/* Login Card */}
        <div className="liquid-glass p-8 animate-rise animate-rise-1">
          <form className="space-y-5" onSubmit={(e) => handleSubmit(e)}>
            <Input
              label="Email address"
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@dynamisrev.com"
            />

            <Input
              label="Password"
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {displayError && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                <p className="text-sm font-medium text-red-600">{displayError}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full flex justify-center py-3 px-4 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-wait transition-all shadow-lg shadow-blue-600/20"
              disabled={isLoading}
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-black/[0.06]" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 text-glass-muted bg-white/80">Protected System</span>
              </div>
            </div>
            <p className="mt-4 text-center text-xs text-glass-muted">
              Authorized Personnel Only
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
